/**
 * Catalogo dei 15 tipi: gruppi, default e fabbrica (CF03/CF04) — CL02.
 *
 * I default qui rispecchiano quelli del contratto
 * (`backend_statistiche/app/schemas/custom_fields.py`). Non sono preferenze di
 * interfaccia: sono i valori che il server applica quando il campo non li
 * specifica, e un default diverso qui produrrebbe una bozza che il server
 * salva in modo diverso da come il progettista l'ha vista.
 *
 * Tenerli in un modulo puro serve a due cose: i configuratori li rendono senza
 * duplicarli, e si possono verificare senza aprire una dialog.
 */

import type { FieldDefinition, FieldType } from "model/CustomFields";
import { currencyCodeForLocale, symbolFor } from "./currencies";

/** La lingua del browser, dove esiste (nei test Node può mancare). */
const browserLocale = (): string | undefined =>
  typeof navigator !== "undefined" ? navigator.language : undefined;

/** I gruppi visivi della master. L'ordine è quello del catalogo. */
export const FIELD_GROUPS = [
  { key: "base", types: ["text", "number", "date", "boolean"] },
  { key: "choices", types: ["select", "multiselect", "radio"] },
  { key: "advanced", types: ["autoincrement", "calculated", "textarea"] },
  {
    key: "validated_financial",
    // L'allegato sta qui perché è lo stesso patto degli altri di questo gruppo:
    // il formato è verificato, e chi lo configura non decide i limiti — li
    // decide l'applicazione (`SUPPORTED_EXTENSIONS`, e la subscription).
    types: ["currency", "percent", "email", "url", "phone", "attachment"],
  },
] as const satisfies readonly { key: string; types: readonly FieldType[] }[];

/** La chiave di traduzione del nome di un tipo: `custom_fields.type.<tipo>`. */
export const typeLabelKey = (type: FieldType): string => `custom_fields.type.${type}`;

/** La chiave del gruppo: `custom_fields.group.<gruppo>`. */
export const groupLabelKey = (group: string): string => `custom_fields.group.${group}`;

/**
 * La configurazione iniziale di ciascun tipo.
 *
 * Un tipo senza configurazione parte da `{}`: il server applica i propri
 * default, e inventarne qui significherebbe contraddirlo.
 */
export function defaultConfigFor(
  type: FieldType,
  locale: string | undefined = browserLocale()
): Record<string, unknown> {
  switch (type) {
    case "number":
      // `precision` non è decorazione: il server rifiuta un valore con più
      // decimali di quelli configurati invece di arrotondarlo.
      return { precision: 2 };
    case "calculated":
      return { outputType: "number", precision: 2 };
    case "date":
      return { includeTime: false, futureOnly: false, displayFormat: "day_month_year" };
    case "boolean":
      return { default: false };
    case "select":
    case "multiselect":
    case "radio":
      // Un campo di scelta senza opzioni non è configurabile: il server ne
      // pretende almeno una.
      return { options: [] };
    case "textarea":
      return { rows: 4, maxLength: 2000, showCounter: false };
    case "currency": {
      // La valuta predefinita segue il browser dell'utente: chi progetta in
      // Svizzera non deve partire dall'euro e cambiarla a mano.
      const currencyCode = currencyCodeForLocale(locale);
      return {
        currencyCode,
        symbol: symbolFor(currencyCode) ?? "€",
        symbolPosition: "before",
        decimalSeparator: ",",
        thousandsSeparator: ".",
        precision: 2,
      };
    }
    case "percent":
      // Il valore canonico è in punti percentuali: `22` è 22%. Il fattore
      // cambia solo la presentazione.
      return { displayMode: "input", displayFactor: "1", min: "0", max: "100", precision: 2 };
    case "url":
      return { httpsOnly: true };
    default:
      return {};
  }
}

/** L'id di un campo: opaco e stabile. Non deriva mai dalla label. */
export const newFieldId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : // Fallback per gli ambienti senza `randomUUID`: la forma resta quella di
      // un UUID, e l'identità resta opaca.
      "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });

/**
 * Un campo nuovo, pronto per la bozza.
 *
 * Nasce con visibilità `process`/`editable`: è la scelta meno sorprendente, e
 * restringerla è una decisione che il progettista prende vedendone l'impatto.
 * Obbligatorietà e `inCard` partono spente: accenderle cambia il comportamento
 * dei processi già avviati, e non deve succedere per omissione.
 */
export function newField(
  type: FieldType,
  position: number,
  label = ""
): FieldDefinition {
  return {
    fieldId: newFieldId(),
    label,
    type,
    position,
    inCard: false,
    requiredOnTask: false,
    requiredOnProcess: false,
    visibility: { scope: "process", access: "editable" },
    config: defaultConfigFor(type),
    deleted: false,
  };
}

/**
 * Il campo è scrivibile da un operatore?
 *
 * I due tipi che il server assegna non lo sono mai: il valore lo produce il
 * registro o la formula. Il configuratore deve mostrarlo, non offrirlo.
 */
export const isServerAssigned = (type: FieldType): boolean =>
  type === "autoincrement" || type === "calculated";

/**
 * Un campo di scelta richiede almeno un'opzione con una chiave distinta.
 *
 * Verificato qui perché il progettista lo scopra mentre configura, invece che
 * al salvataggio con un errore del server.
 */
export function choiceConfigIssues(config: Record<string, unknown>): string[] {
  const options = Array.isArray(config.options) ? config.options : [];
  if (options.length === 0) return ["custom_fields.error.validation_failed"];

  const ids = options.map((option) =>
    String((option as { id?: unknown })?.id ?? "")
  );
  if (ids.some((id) => id.trim() === "")) return ["custom_fields.error.duplicate_option"];
  if (new Set(ids).size !== ids.length) return ["custom_fields.error.duplicate_option"];
  return [];
}

/**
 * Il default dichiarato esiste ancora fra le opzioni?
 *
 * Un default che punta a un'opzione rimossa renderebbe il campo inutilizzabile
 * al primo utilizzo, con l'errore lontano dalla sua causa.
 */
export function defaultIsAvailable(config: Record<string, unknown>): boolean {
  const options = Array.isArray(config.options) ? config.options : [];
  const ids = new Set(options.map((option) => String((option as { id?: unknown })?.id ?? "")));

  const declared = config.default;
  if (declared === undefined || declared === null) return true;
  if (Array.isArray(declared)) return declared.every((id) => ids.has(String(id)));
  return ids.has(String(declared));
}
